import { EcoaContent } from "@components/common/EconContent";
import React, { ReactNode } from "react";
import styled from "@emotion/styled";
import { EcoaFontSize, useLangContext } from "@layouts/home/LangContext";
import { LabelsStyleSpec } from "maven-lib/dist/consts/edc";
import { CrfItemAlignment } from "maven-lib/dist/consts/edc/crf";
import { getCrfLabel } from "maven-lib/dist/consts/edc/lib";
import { HbDivider } from "@maven-surface/components/base/HbDivider";
import { HbParagraph } from "@maven-surface/components/base/HbParagraph";
import { IconPackageName } from "@maven-surface/components/interface/Icon";

interface CustomizedStyleLabelType {
  bold?: boolean;
  italic?: boolean;
  color?: string;
  underline?: boolean;
}

const CustomizedStyleLabel = styled.label<CustomizedStyleLabelType>`
  font-weight: ${(props) => (props.bold ? "bold" : "normal")};
  font-style: ${(props) => (props.italic ? "italic" : "normal")};
  color: ${(props) => props.color || "inherit"};
  text-decoration-line: ${(props) => (props.underline ? "underline" : "none")};
`;

interface CustomizedStyleLabelComponentProps {
  children: string;
  lineBreak?: {
    before?: boolean;
    after?: boolean;
  };
  bold?: boolean;
  italic?: boolean;
  color?: `#${string}`;
  underline?: boolean;
}

interface RenderedQuestionProps {
  question: string | LabelsStyleSpec;
  icon?: IconPackageName;
  text?: string;
  questionBold?: boolean;
  questionAlign?: CrfItemAlignment;
  textBold?: boolean;
  fontSize?: EcoaFontSize;
}

export const CustomizedStyleLabelComponent = ({
  children,
  lineBreak,
  italic,
  bold,
  color,
  underline,
}: CustomizedStyleLabelComponentProps) => {
  return (
    <>
      {lineBreak?.before && <br />}
      <CustomizedStyleLabel italic={italic} bold={bold} color={color} underline={underline}>
        {children}
      </CustomizedStyleLabel>
      {lineBreak?.after && <br />}
    </>
  );
};

export const RenderedQuestion = (props: RenderedQuestionProps) => {
  const { question, fontSize, questionBold, icon, questionAlign } = props;
  const questionSize = fontSize === "LARGE" ? "8X-LARGE" : fontSize === "MEDIUM" ? "5X-LARGE" : "3X-LARGE";

  if (typeof question === "string") {
    return <HbParagraph text={question} size={questionSize} bold={questionBold} color="GRAY" icon={icon} align={questionAlign} />;
  }

  return (
    <HbParagraph size={questionSize} color="GRAY" icon={icon} align={questionAlign}>
      {question.map((labelStyle, index) => {
        if (typeof labelStyle === "string") {
          return <CustomizedStyleLabelComponent key={index}>{labelStyle}</CustomizedStyleLabelComponent>;
        }

        return (
          <CustomizedStyleLabelComponent
            key={index}
            bold={labelStyle.bold}
            italic={labelStyle.italic}
            lineBreak={labelStyle.lineBreak}
            color={labelStyle.color?.value}
            underline={labelStyle.underline}
          >
            {getCrfLabel(labelStyle.text)}
          </CustomizedStyleLabelComponent>
        );
      })}
    </HbParagraph>
  );
};

interface VisitQuestionnaireWrapperProps {
  title?: string;
  question?: string | LabelsStyleSpec;
  icon?: IconPackageName;
  text?: string;
  children: ReactNode;
  questionBold?: boolean;
  questionAlign?: CrfItemAlignment;
  textBold?: boolean;
}

export function VisitQuestionnaireWrapper({
  children,
  title,
  question,
  icon,
  text,
  questionBold = true,
  questionAlign = "LEFT",
  textBold = true,
}: VisitQuestionnaireWrapperProps) {
  const { fontSize } = useLangContext();
  const textSize = fontSize === "LARGE" ? "5X-LARGE" : fontSize === "MEDIUM" ? "3X-LARGE" : "X-LARGE";

  return (
    <EcoaContent barTitle={title} fontSize={fontSize}>
      {question && (
        <RenderedQuestion question={question} fontSize={fontSize} questionBold={questionBold} icon={icon} questionAlign={questionAlign} />
      )}
      {text && <HbParagraph text={text} size={textSize} bold={textBold} />}
      {(question || text) && <HbDivider />}
      {children}
    </EcoaContent>
  );
}
